import { useValuationStore } from '@/store/valuationStore'
import { useRefStore } from '@/store/refStore'
import { useAttributeStore } from '@/store/attributeStore'
import { SECTIONS } from '@/data/sections.data'
import { getAiResults } from '@/services/claimService'
import { claimResponseToFormValues } from '@/utils/claimPayloadMapper'
import { preloadRefCategories } from '@/utils/preloadRefCategories'
import { deleteImageRecord } from '@/services/idbService'
import { log } from '@/utils/logger'
import type { ClaimGetResponse } from '@/types/claim'

export async function loadClaimForEditing(
  userId: string,
  claimData: ClaimGetResponse
): Promise<void> {
  const claimId = String(claimData.id)
  const caseId = claimData.caseId ?? null

  // Guards useConditionalFields' Use/Type change-detection effects: while this flag is
  // set, the '' → loaded-value transition on standard-equipment.use/type (which happens
  // as soon as setClaimData below populates the form) is claim data loading in, not a
  // real user edit — so those effects must not treat it as a change and reset
  // body-information out from under the data we're about to set.
  useValuationStore.getState().setIsLoadingClaim(true)

  try {
    // Evict stale form-recognizer photos from previous session before loading the
    // correct ones for this claim. filterPhotosBySectionImages skips form-recognizer
    // so they must be cleared here. Do not delete from server — blobs belong to their
    // respective claims and the server is the source of truth.
    const stale = useValuationStore.getState().photos.filter((p) => p.blobSection === 'form-recognizer')
    if (stale.length > 0) {
      stale.forEach((p) => {
        URL.revokeObjectURL(p.blobUrl)
        deleteImageRecord(p.id).catch(() => {})
      })
      const staleIds = new Set(stale.map((p) => p.id))
      useValuationStore.setState((s) => ({ photos: s.photos.filter((p) => !staleIds.has(p.id)) }))
    }

    await preloadRefCategories(claimData)
    const getRefLabel = useRefStore.getState().getLabelById
    const formValues = claimResponseToFormValues(claimData, getRefLabel, useAttributeStore.getState().cache)

    useValuationStore.getState().setDraftId(claimId)
    if (caseId) useValuationStore.getState().setCaseId(caseId)
    useValuationStore.getState().setStatus(claimData.statusId === 2 ? 'submitted' : 'draft')
    useValuationStore.getState().setClaimData(claimId, formValues)
    useValuationStore.getState().setIsDirty(false)
    useValuationStore.getState().setHasTriedSubmit(false)
    useValuationStore.getState().setHasTriedSave(false)
    useValuationStore.setState({
      sectionStates: Object.fromEntries(SECTIONS.map((s) => [s.id, { expanded: s.id === 'basic-information', complete: false, touched: false }])),
    })

    if (caseId) {
      let aiResData: Awaited<ReturnType<typeof getAiResults>>['data'] | null = null
      try {
        const aiRes = await getAiResults(userId, caseId)
        if (aiRes.success && aiRes.data) {
          aiResData = aiRes.data
          useValuationStore.getState().applyFieldMetadata(aiResData.fieldMetadata ?? {})
          log.info('loadClaimForEditing AI field metadata applied', { caseId })
        }
      } catch {
        log.warn('loadClaimForEditing AI/OCR fetch failed (non-fatal)', { caseId })
      }

      try {
        await useValuationStore.getState().loadAllPhotosFromServer(userId, caseId)
        const sectionImages = claimData.vehicle?.sectionImages ?? []
        await useValuationStore.getState().filterPhotosBySectionImages(sectionImages)
        if (aiResData) {
          useValuationStore.getState().restoreAiResults(aiResData)
          log.info('loadClaimForEditing AI/OCR results restored', { caseId })
        }
      } catch (e) {
        log.error('loadClaimForEditing photo load failed (non-fatal)', e instanceof Error ? e : new Error(String(e)), { caseId })
      }
    }

    try {
      await useValuationStore.getState().flushSave()
      useValuationStore.getState().markHydrated()
    } catch {
      useValuationStore.getState().markHydrated()
    }
  } finally {
    useValuationStore.getState().setIsLoadingClaim(false)
  }
}
